import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getProjectStaffing, getTeamMembers, setStaffingWeeks, updateScenarioProject,
  addScenarioProjectRow, deleteScenarioProjectRow,
} from '../../api/client'
import ProjectMonthlyChart from './ProjectMonthlyChart'

const num = (n) => (Number.isInteger(n) ? String(n) : Number(n).toFixed(1))

/**
 * Side panel that opens when you click a project of a scenario: its dates,
 * who is on it, the monthly chart and the week-by-week hours -- all written to
 * the scenario's project, never to the real one (c_level only; the server
 * enforces it).
 */
export default function ScenarioProjectPanel({ scenario, project, onClose, onChanged }) {
  const { t } = useTranslation(['clevel', 'common'])
  const [staffing, setStaffing] = useState(null)
  const [members, setMembers] = useState([])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [dates, setDates] = useState({ start: project.start_date || '', end: project.end_date || '' })
  const [newMember, setNewMember] = useState('')
  const [perWeek, setPerWeek] = useState('40')

  const load = useCallback(async () => {
    try {
      const data = await getProjectStaffing(project.id, scenario.id)
      setStaffing(data && Array.isArray(data.rows) ? data : { rows: [], weeks: [] })
    } catch (e) {
      setError(e.response?.data?.detail || t('scenarios.panel.loadFailed'))
    }
  }, [project.id, scenario.id, t])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    getTeamMembers({ plannable: true }).then(ms => setMembers(Array.isArray(ms) ? ms : [])).catch(() => {})
  }, [])

  const run = async (fn) => {
    setBusy(true); setError('')
    try { await fn(); await load() } catch (e) {
      setError(e.response?.data?.detail || t('scenarios.panel.saveFailed'))
    }
    setBusy(false)
  }

  const saveDates = () => run(async () => {
    await updateScenarioProject(scenario.id, project.id, {
      name: project.name, start_date: dates.start || null, end_date: dates.end || null })
    onChanged?.()
  })

  const addPerson = () => run(async () => {
    const row = await addScenarioProjectRow(scenario.id, project.id, { team_member_id: Number(newMember) })
    const n = parseFloat(perWeek)
    if (n > 0) await setStaffingWeeks(project.id, row.id, { hours_per_week: n }, scenario.id)
    setNewMember('')
  })

  const fillRow = (row, hours) => run(() =>
    setStaffingWeeks(project.id, row.id, { hours_per_week: hours }, scenario.id))
  const setCell = (row, week, value) => {
    const n = parseFloat(value)
    if (Number.isNaN(n) || n < 0 || n === week.hours) return
    return run(() => setStaffingWeeks(project.id, row.id, { week_start: week.monday, hours: n }, scenario.id))
  }
  const removeRow = (row) => {
    if (!confirm(t('scenarios.panel.removeConfirm', { name: row.name }))) return
    return run(() => deleteScenarioProjectRow(scenario.id, project.id, row.id))
  }

  const rows = staffing?.rows || []
  const weeks = staffing?.weeks || []
  const taken = new Set(rows.map(r => r.team_member_id))
  const available = members.filter(m => !taken.has(m.id) && !m.es_equipo)
  const noDates = !dates.start || !dates.end || !project.start_date || !project.end_date

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-white h-full overflow-y-auto shadow-xl" style={{ width: 'min(1000px, 96vw)' }}>
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              <span className="text-amber-700" title={t('scenarios.badgeHint')}>◆ </span>
              {project.code ? `${project.code} · ${project.name}` : project.name}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {t('scenarios.panel.subtitle', { scenario: scenario.name })}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        <div className="px-6 py-4 space-y-6">
          {error && <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('scenarios.panel.window')}</h3>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-xs text-gray-600">{t('scenarios.panel.start')}
                <input type="date" value={dates.start} onChange={e => setDates(d => ({ ...d, start: e.target.value }))}
                  className="block mt-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm" />
              </label>
              <label className="text-xs text-gray-600">{t('scenarios.panel.end')}
                <input type="date" value={dates.end} onChange={e => setDates(d => ({ ...d, end: e.target.value }))}
                  className="block mt-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm" />
              </label>
              <button onClick={saveDates} disabled={busy}
                className="text-xs px-3 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-40">
                {t('scenarios.panel.saveDates')}
              </button>
            </div>
            {noDates && <p className="text-xs text-amber-700 mt-2">{t('scenarios.panel.needDates')}</p>}
          </section>

          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">{t('scenarios.panel.chart')}</h3>
            <ProjectMonthlyChart staffing={staffing} />
          </section>

          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('scenarios.panel.people')}</h3>
            <div className="flex flex-wrap items-end gap-2 mb-3">
              <select value={newMember} onChange={e => setNewMember(e.target.value)}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
                <option value="">{t('scenarios.panel.pickPerson')}</option>
                {available.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <label className="text-xs text-gray-600 flex items-center gap-1">
                <input type="number" min="0" step="1" value={perWeek} onChange={e => setPerWeek(e.target.value)}
                  className="w-16 border border-gray-200 rounded-lg px-2 py-1.5 text-sm" />
                {t('scenarios.panel.hoursPerWeek')}
              </label>
              <button onClick={addPerson} disabled={busy || !newMember || noDates}
                className="text-xs px-3 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-40">
                {t('scenarios.panel.addPerson')}
              </button>
            </div>

            {rows.length === 0 ? (
              <p className="text-sm text-gray-400 py-3">{t('scenarios.panel.noPeople')}</p>
            ) : (
              <div className="overflow-x-auto border border-gray-100 rounded-lg">
                <table className="text-xs border-collapse">
                  <thead>
                    <tr className="text-gray-500">
                      <th className="sticky left-0 bg-white text-left font-medium px-3 py-2 min-w-[190px]">
                        {t('scenarios.panel.person')}
                      </th>
                      <th className="px-2 py-2 font-medium">{t('scenarios.panel.total')}</th>
                      {weeks.map(w => (
                        <th key={w.monday} className="px-1 py-2 font-normal text-gray-400 min-w-[52px]">
                          {w.monday.slice(8)}/{w.monday.slice(5, 7)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(row => (
                      <tr key={row.id} className="border-t border-gray-50">
                        <td className="sticky left-0 bg-white px-3 py-1.5">
                          <div className="text-gray-900 text-sm">{row.name}</div>
                          <div className="flex gap-2 text-[11px] text-gray-400">
                            <span>{row.role}</span>
                            <button onClick={() => fillRow(row, Number(row.weekly_hours) || 40)} className="hover:text-gray-700">
                              {t('scenarios.panel.fillMax')}
                            </button>
                            <button onClick={() => fillRow(row, null)} className="hover:text-gray-700">
                              {t('scenarios.panel.clear')}
                            </button>
                            <button onClick={() => removeRow(row)} className="hover:text-red-500">
                              {t('common:actions.delete')}
                            </button>
                          </div>
                        </td>
                        <td className="px-2 text-center font-medium text-gray-700">{num(row.total_hours)}</td>
                        {row.weeks.map(w => (
                          <td key={w.monday} className="px-0.5 py-1">
                            <input type="number" min="0" max={w.max_hours} step="1" defaultValue={w.hours || ''}
                              key={`${row.id}-${w.monday}-${w.hours}`}
                              onBlur={e => setCell(row, w, e.target.value === '' ? 0 : e.target.value)}
                              className="w-12 border border-gray-100 rounded px-1 py-1 text-center text-xs" />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  )
}
